import { type InputHTMLAttributes, useEffect } from 'react';

import { Label } from 'cpool-components/general/Label';
import { NumberInput } from 'cpool-components/input/NumberInput';
import { RadioButton } from 'cpool-components/input/RadioButton';
import { TextInput } from 'cpool-components/input/TextInput';
import {
    BUILDING_CONSTRUCTION_TYPES_IN_DISPLAY_ORDER,
    BUILDING_DESCRIPTION_PROPERTY_TYPES,
    BUILDING_DESCRIPTION_PROPERTY_TYPES_IN_DISPLAY_ORDER,
    BUILDING_DESCRIPTION_PROPERTY_TYPES_WITH_UNIT_COUNT,
    MEASURING_UNITS,
} from 'cpool-constants';
import type { Path } from 'react-hook-form';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuCalendar } from 'react-icons/lu';

import type { StepProps } from './types';
import { FormField } from '../../../shared/inputs/FormField';
import { FormBooleanSegmentedField, OptionGroupFieldset, OptionGroupGrid } from '../FormControls';
import type { SchemaRealEstatesForm } from '~/features/leadDetails/schemas/realEstates.schema';
import { GERMAN_POSTAL_CODE_ERROR_KEY, isGermanPostalCode } from '~/features/leadDetails/utils/germanPostalCode';
import { keepDigits } from '~/features/leadDetails/utils/keepDigits';

type StringFieldProps = {
    className?: string;
    disabled?: boolean;
    inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
    maxLength?: number;
    name: Path<SchemaRealEstatesForm>;
    normalize?: (value: string) => string;
    placeholder: string;
    validateGermanPostalCode?: boolean;
};

type NumberFieldProps = {
    compact?: boolean;
    disabled?: boolean;
    hint?: string;
    label: string;
    max?: number;
    min?: number;
    name: Path<SchemaRealEstatesForm>;
    placeholder?: string;
    unit?: React.ReactNode;
};

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h6 className="text-sm font-bold text-midnight">{children}</h6>
);

const StringField: React.FC<StringFieldProps> = ({
    className,
    disabled,
    inputMode,
    maxLength,
    name,
    normalize,
    placeholder,
    validateGermanPostalCode,
}) => {
    const { control } = useFormContext<SchemaRealEstatesForm>();
    const { t } = useTranslation();
    return (
        <FormField<SchemaRealEstatesForm, string | null> control={control} name={name}>
            {({ error, onBlur, onChange, value }) => (
                <TextInput
                    id={name}
                    aria-label={placeholder}
                    autoComplete="off"
                    className={className}
                    data-1p-ignore
                    inputMode={inputMode}
                    maxLength={maxLength}
                    value={value ?? ''}
                    onBlur={onBlur}
                    onChange={(event) => {
                        const next = normalize ? normalize(event.target.value) : event.target.value;
                        onChange(next || null);
                    }}
                    placeholder={placeholder}
                    disabled={disabled}
                    error={
                        validateGermanPostalCode && !isGermanPostalCode(value) ? t(GERMAN_POSTAL_CODE_ERROR_KEY) : error
                    }
                />
            )}
        </FormField>
    );
};

const NumberField: React.FC<NumberFieldProps> = ({
    compact,
    disabled,
    hint,
    label,
    max,
    min,
    name,
    placeholder,
    unit,
}) => {
    const { control } = useFormContext<SchemaRealEstatesForm>();
    const hintId = hint ? `${name}-hint` : undefined;
    const accessibleLabel = typeof unit === 'string' ? `${label} (${unit})` : undefined;
    const fieldClassName = compact
        ? 'w-fit min-w-0 [&>div]:min-w-0 [&>div]:flex-none'
        : 'w-full min-w-0 flex-col sm:flex-row [&>div]:min-w-0';
    const fieldInputClassName = compact ? 'w-20 min-w-0' : 'min-w-0';
    const fieldLabelClassName = compact
        ? 'h-8 w-auto shrink-0 justify-start whitespace-nowrap'
        : 'h-auto min-h-8 w-full shrink-0 justify-start sm:w-[175px]';
    return (
        <div className="space-y-1">
            <FormField<SchemaRealEstatesForm, number | null> control={control} name={name}>
                {({ error, onChange, value }) => (
                    <NumberInput
                        id={name}
                        label={label}
                        labelClassName={fieldLabelClassName}
                        className={fieldClassName}
                        inputClassName={fieldInputClassName}
                        value={value}
                        onChange={onChange}
                        min={min}
                        max={max}
                        allowDecimals={false}
                        measuringUnit={unit}
                        placeholder={placeholder}
                        disabled={disabled}
                        error={error}
                        aria-describedby={hintId}
                        aria-label={accessibleLabel}
                    />
                )}
            </FormField>
            {hint && (
                <p id={hintId} className="px-3.75 text-xs italic text-midnight">
                    {hint}
                </p>
            )}
        </div>
    );
};

const PairedAddressField: React.FC<{
    disabled?: boolean;
    first: Omit<StringFieldProps, 'disabled'>;
    label: string;
    second: Omit<StringFieldProps, 'disabled'>;
}> = ({ disabled, first, label, second }) => (
    <div className="flex flex-col items-start gap-2.5 sm:flex-row">
        <Label className="h-8 w-full shrink-0 justify-start sm:w-[175px]">{label}</Label>
        <div className="flex w-full min-w-0 items-start gap-2">
            <StringField {...first} disabled={disabled} />
            <StringField {...second} disabled={disabled} />
        </div>
    </div>
);

const OptionRow: React.FC<{
    checked: boolean;
    disabled?: boolean;
    label: string;
    name: string;
    onChange: () => void;
    value: string;
}> = ({ checked, disabled, label, name, onChange, value }) => (
    <RadioButton
        id={`${name}-${value}`}
        name={name}
        label={label}
        value={value}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        wrapperClassName="min-h-8 w-full py-1"
    />
);

export const AddressAndPropertyTypeStep: React.FC<StepProps> = ({ disabled, realEstateIndex }) => {
    const { t } = useTranslation();
    const { control, getValues, setValue } = useFormContext<SchemaRealEstatesForm>();
    const prefix = `immobilien.${realEstateIndex}.baubeschreibung.anschriftUndObjektart` as const;
    const propertyType = useWatch({ control, name: `${prefix}.objektart` });

    useEffect(() => {
        if (
            propertyType !== BUILDING_DESCRIPTION_PROPERTY_TYPES.EIGENTUMSWOHNUNG &&
            getValues(`${prefix}.etage`) != null
        ) {
            setValue(`${prefix}.etage`, null, { shouldDirty: true, shouldValidate: true });
        }
        if (
            (!propertyType || !BUILDING_DESCRIPTION_PROPERTY_TYPES_WITH_UNIT_COUNT.has(propertyType)) &&
            getValues(`${prefix}.wohneinheitenAnzahl`) != null
        ) {
            setValue(`${prefix}.wohneinheitenAnzahl`, null, { shouldDirty: true, shouldValidate: true });
        }
    }, [getValues, prefix, propertyType, setValue]);

    return (
        <div className="space-y-10">
            <section className="space-y-4">
                <SectionHeading>{t('LIVING_SITUATION_DATA.ADDRESS')}</SectionHeading>
                <div className="grid min-w-0 grid-cols-1 gap-x-8 gap-y-2 lg:grid-cols-2">
                    <div className="min-w-0 space-y-2">
                        <PairedAddressField
                            label={t('REAL_ESTATE.BUILDING_DESCRIPTION.FIELDS.STREET_AND_HOUSE_NUMBER')}
                            first={{
                                className: 'min-w-0 flex-1',
                                name: `${prefix}.anschrift.strasse`,
                                placeholder: t('ADDRESS.STREET'),
                            }}
                            second={{
                                className: 'w-20 shrink-0',
                                name: `${prefix}.anschrift.hausnummer`,
                                placeholder: t('ADDRESS.HOUSE_NUMBER_SHORT'),
                            }}
                            disabled={disabled}
                        />
                        <PairedAddressField
                            label={t('REAL_ESTATE.BUILDING_DESCRIPTION.FIELDS.ZIP_AND_CITY')}
                            first={{
                                className: 'w-20 shrink-0',
                                inputMode: 'numeric',
                                maxLength: 5,
                                name: `${prefix}.anschrift.plz`,
                                normalize: (value) => keepDigits(value, 5),
                                placeholder: t('ADDRESS.ZIP_CODE_SHORT'),
                                validateGermanPostalCode: true,
                            }}
                            second={{
                                className: 'min-w-0 flex-1',
                                name: `${prefix}.anschrift.ort`,
                                placeholder: t('ADDRESS.LOCATION'),
                            }}
                            disabled={disabled}
                        />
                        <NumberField
                            name={`${prefix}.baujahr`}
                            label={t('REAL_ESTATE.BUILDING_DESCRIPTION.FIELDS.CONSTRUCTION_YEAR')}
                            min={1000}
                            max={9999}
                            placeholder={t('REAL_ESTATE.BUILDING_DESCRIPTION.FIELDS.YEAR_PLACEHOLDER')}
                            unit={<LuCalendar className="size-4" />}
                            disabled={disabled}
                        />
                        <FormBooleanSegmentedField
                            name={`${prefix}.denkmalschutz`}
                            label={t('REAL_ESTATE.BUILDING_DESCRIPTION.FIELDS.MONUMENT_PROTECTION')}
                            disabled={disabled}
                        />
                        {propertyType === BUILDING_DESCRIPTION_PROPERTY_TYPES.EIGENTUMSWOHNUNG && (
                            <NumberField
                                name={`${prefix}.etage`}
                                label={t('REAL_ESTATE.BUILDING_DESCRIPTION.FIELDS.FLOOR')}
                                hint={t('REAL_ESTATE.BUILDING_DESCRIPTION.FIELDS.FLOOR_HINT')}
                                disabled={disabled}
                            />
                        )}
                    </div>
                    <div className="min-w-0 space-y-2">
                        <NumberField
                            name={`${prefix}.grundstuecksgroesse`}
                            label={t('REAL_ESTATE.BUILDING_DESCRIPTION.FIELDS.LOT_SIZE')}
                            min={0}
                            unit={MEASURING_UNITS.SQUARE_METERS}
                            hint={t('REAL_ESTATE.BUILDING_DESCRIPTION.FIELDS.LOT_SIZE_HINT')}
                            disabled={disabled}
                        />
                        <FormBooleanSegmentedField
                            name={`${prefix}.pacht`}
                            label={t('REAL_ESTATE.BUILDING_DESCRIPTION.FIELDS.LEASEHOLD')}
                            disabled={disabled}
                        />
                    </div>
                </div>
            </section>

            <div className="space-y-3">
                <Controller
                    control={control}
                    name={`${prefix}.objektart`}
                    render={({ field }) => (
                        <OptionGroupFieldset label={t('REAL_ESTATE.BUILDING_DESCRIPTION.GROUPS.PROPERTY_TYPE')}>
                            <OptionGroupGrid>
                                {BUILDING_DESCRIPTION_PROPERTY_TYPES_IN_DISPLAY_ORDER.map((value) => (
                                    <OptionRow
                                        key={value}
                                        name={`${prefix}.objektart`}
                                        label={t(`REAL_ESTATE.BUILDING_DESCRIPTION.OPTIONS.PROPERTY_TYPE.${value}`)}
                                        value={value}
                                        checked={field.value === value}
                                        onChange={() => field.onChange(value)}
                                        disabled={disabled}
                                    />
                                ))}
                            </OptionGroupGrid>
                            {field.value && BUILDING_DESCRIPTION_PROPERTY_TYPES_WITH_UNIT_COUNT.has(field.value) && (
                                <div className="clear-both mt-3">
                                    <NumberField
                                        name={`${prefix}.wohneinheitenAnzahl`}
                                        label={t('REAL_ESTATE.BUILDING_DESCRIPTION.FIELDS.RESIDENTIAL_UNITS')}
                                        compact
                                        min={0}
                                        disabled={disabled}
                                    />
                                </div>
                            )}
                        </OptionGroupFieldset>
                    )}
                />

                <Controller
                    control={control}
                    name={`${prefix}.bauweise`}
                    render={({ field }) => (
                        <OptionGroupFieldset label={t('REAL_ESTATE.BUILDING_DESCRIPTION.GROUPS.CONSTRUCTION_TYPE')}>
                            <OptionGroupGrid>
                                {BUILDING_CONSTRUCTION_TYPES_IN_DISPLAY_ORDER.map((value) => (
                                    <OptionRow
                                        key={value}
                                        name={`${prefix}.bauweise`}
                                        label={t(`REAL_ESTATE.BUILDING_DESCRIPTION.OPTIONS.CONSTRUCTION_TYPE.${value}`)}
                                        value={value}
                                        checked={field.value === value}
                                        onChange={() => field.onChange(value)}
                                        disabled={disabled}
                                    />
                                ))}
                            </OptionGroupGrid>
                        </OptionGroupFieldset>
                    )}
                />
            </div>
        </div>
    );
};
